import { apiClient } from './api-client';

export interface NotificationItem {
  id: string;
  type: string;
  title: string;
  message: string;
  status: 'PENDING' | 'READ';
  payloadJson: unknown;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationListResponse {
  items: NotificationItem[];
  /** Unread across the whole visible scope, not just the returned page. */
  unreadCount: number;
}

/**
 * B6.17 — Notification Center client.
 *
 * Every endpoint is self-scoped server-side: the API derives tenant and user
 * from the verified JWT, and none of these calls sends a userId. There is
 * deliberately no "list for user X" — no such endpoint exists.
 *
 * `apiClient`'s response interceptor already unwraps the `{ data, meta }`
 * envelope, so `r.data` is the payload. Arrays are guarded per CLAUDE.md.
 */
export const notificationService = {
  list: (opts?: { limit?: number; unreadOnly?: boolean }): Promise<NotificationListResponse> =>
    apiClient
      .get<NotificationListResponse>('/notifications', {
        params: { limit: opts?.limit, unreadOnly: opts?.unreadOnly },
      })
      .then((r) => ({
        items: Array.isArray(r.data?.items) ? r.data.items : [],
        unreadCount: r.data?.unreadCount ?? 0,
      })),

  unreadCount: (): Promise<number> =>
    apiClient
      .get<{ unreadCount: number }>('/notifications/unread-count')
      .then((r) => r.data?.unreadCount ?? 0),

  markRead: (id: string): Promise<void> =>
    apiClient.patch<void>(`/notifications/${id}/read`).then(() => undefined),

  markAllRead: (): Promise<number> =>
    apiClient
      .post<{ updated: number }>('/notifications/read-all')
      .then((r) => r.data?.updated ?? 0),
};
